import { memo, useMemo } from 'react'

interface TaskDef {
  liveCheckinTaskType: string
  emoji?: string | null
  internalName?: string | null
}

type Action = 'checkin' | 'share' | 'games' | null

// Mapa tipo de tarefa -> ação ao clicar.
// As demais (do_live, duration_live, receive_point, new_follower) são passivas
// (acontecem sozinhas) -> aparecem no card mas não são clicáveis.
const TASK_ACTION: Record<string, Action> = {
  check_in: 'checkin',
  invite_user: 'share',
  play_rocket: 'games',
  play_gold_miner: 'games',
  play_slot: 'games',
  play_spin_wheel: 'games',
  play_luck_box: 'games',
}

// O gateway entrega os valores de enum em camelCase (ex.: 'checkIn'); as chaves
// acima são snake_case (igual ao banco). Normaliza pra casar os dois.
function toSnake(type: string): string {
  return type.replace(/([A-Z])/g, '_$1').toLowerCase()
}

function actionFor(type: string): Action {
  return TASK_ACTION[type] ?? TASK_ACTION[toSnake(type)] ?? null
}

function postToRN(payload: Record<string, unknown>) {
  if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
    // @ts-expect-error injetado pelo react-native-webview
    window.ReactNativeWebView.postMessage(JSON.stringify(payload))
  }
}

interface Props {
  tasks: TaskDef[]
  onCheckin: () => void
  isCheckingIn: boolean
  checkedInToday: boolean
}

export const TaskActionsCard = memo(
  ({ tasks, onCheckin, isCheckingIn, checkedInToday }: Props) => {
    // Só os tipos ACIONÁVEIS entram no card (têm ação ao clicar): check_in,
    // invite_user e os jogos. As tarefas passivas (do_live, duration_live,
    // receive_point, new_follower) acontecem sozinhas (ações do host) e ficam
    // de fora. win_battle/PK também (desativado). Dedupe por tipo (defaults vêm
    // por nível).
    const uniqueTasks = useMemo(() => {
      const seen = new Set<string>()
      const out: TaskDef[] = []
      for (const t of tasks) {
        const type = t.liveCheckinTaskType
        if (!type || !actionFor(type) || seen.has(type)) continue
        seen.add(type)
        out.push(t)
      }
      return out
    }, [tasks])

    function handleClick(type: string) {
      const action = actionFor(type)
      if (action === 'checkin') return onCheckin()
      if (action === 'share') return postToRN({ type: 'share-live' })
      if (action === 'games') return postToRN({ type: 'open-games' })
    }

    if (!uniqueTasks.length) return null

    return (
      <section className="rounded-2xl border border-white/10 bg-white/[0.06] p-3 backdrop-blur-md">
        <header className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-wide text-white/90">
            Ações rápidas
          </h3>
          <span className="text-[10px] text-white/50">toque para ir</span>
        </header>

        <div className="grid grid-cols-4 gap-2">
          {uniqueTasks.map((t) => {
            const action = actionFor(t.liveCheckinTaskType)
            const isCheckinBtn = action === 'checkin'
            const disabled = isCheckinBtn && (isCheckingIn || checkedInToday)

            return (
              <button
                type="button"
                key={t.liveCheckinTaskType}
                onClick={() => handleClick(t.liveCheckinTaskType)}
                disabled={disabled}
                title={t.internalName ?? t.liveCheckinTaskType}
                className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border p-1 text-center transition active:scale-95 ${
                  disabled
                    ? 'border-white/10 bg-white/[0.05] opacity-60'
                    : isCheckinBtn
                      ? 'border-hug-purple/50 bg-gradient-to-br from-hug-purple/25 to-hug-lilac/15 shadow-[0_0_14px_var(--hug-glow)] hover:from-hug-purple/35 hover:to-hug-lilac/25'
                      : 'border-white/10 bg-white/[0.06] hover:border-hug-purple/40 hover:bg-hug-purple/15'
                }`}
              >
                <span className="text-2xl leading-none">{t.emoji ?? '•'}</span>
                <span className="line-clamp-2 text-[9px] font-medium leading-tight text-white/85">
                  {isCheckinBtn && checkedInToday
                    ? 'Feito hoje'
                    : (t.internalName ?? t.liveCheckinTaskType)}
                </span>
              </button>
            )
          })}
        </div>
      </section>
    )
  },
)

TaskActionsCard.displayName = 'TaskActionsCard'
